/**
 * Singleton toast (ported from xdface/library, restyled with vault tokens).
 * Lazily creates a fixed bottom-center container; a new toast replaces the
 * previous one. Text is provided by the caller.
 */

const TOAST_ID = 'vault-toast';

export interface ToastOptions {
  /** Label for an optional action button (e.g. "Deshacer"). */
  actionLabel?: string;
  onAction?: () => void;
  /** Visible time in ms before the toast dismisses itself (default 2000, 5000 with action). */
  duration?: number;
}

export function showToast(message: string, options: ToastOptions = {}): void {
  const { actionLabel, onAction, duration = actionLabel ? 5000 : 2000 } = options;
  const existing = document.getElementById(TOAST_ID);
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.id = TOAST_ID;
  Object.assign(toast.style, {
    position: 'fixed',
    bottom: '140px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'var(--xd-raised)',
    color: 'var(--xd-fg)',
    padding: '10px 20px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '500',
    zIndex: 'var(--xd-z-toast, 70)',
    boxShadow: 'var(--xd-soft-shadow-md)',
    transition: 'opacity 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  });
  toast.setAttribute('role', 'status');

  const text = document.createElement('span');
  text.textContent = message;
  toast.appendChild(text);

  let dismissed = false;
  const dismiss = () => {
    if (dismissed) return;
    dismissed = true;
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 300);
  };

  if (actionLabel) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = actionLabel;
    Object.assign(btn.style, {
      background: 'none',
      border: 'none',
      color: 'var(--xd-action)',
      font: 'inherit',
      fontWeight: '600',
      cursor: 'pointer',
      padding: '0',
      textDecoration: 'underline',
    });
    btn.addEventListener('click', () => {
      onAction?.();
      dismiss();
    });
    toast.appendChild(btn);
  }

  document.body.appendChild(toast);
  setTimeout(dismiss, duration);
}
